import React, { useState } from 'react';
import { Search } from 'lucide-react';
import type { Database } from '../../lib/database.types';
import type { ServiceType, ApplianceBrand } from '../../types';
import { useCustomerContacts, useTechnicians } from '../../lib/hooks';

type Customer = Database['public']['Tables']['customers']['Row'];

interface AddWorkOrderModalProps {
  onClose: () => void;
  onAdd: (workOrder: any) => Promise<void>;
  customers: Customer[];
  services: ServiceType[];
  brands: ApplianceBrand[];
}

export const AddWorkOrderModal: React.FC<AddWorkOrderModalProps> = ({
  onClose,
  onAdd,
  customers,
  services,
  brands,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<string>('');
  const [selectedService, setSelectedService] = useState<ServiceType | ''>('');
  const { contacts: customerContacts, loading: contactsLoading } = useCustomerContacts(selectedCustomer || '');
  const { technicians } = useTechnicians();
  const [formData, setFormData] = useState({
    status: 'Received' as const,
    priority: 'medium' as const,
    svo: '',
    model_number: '',
    serial_number: '',
    svo: '',
    model_number: '',
    serial_number: '',
    description: '',
    notes: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const filteredCustomers = customers.filter(customer => 
    `${customer.first_name} ${customer.last_name} ${customer.email} ${customer.phone}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const statusOptions = [
    'Received',
    'Customer Notified',
    'Awaiting Customer Response',
    'Waiting for Parts',
    'Technician Ordering Parts',
    'Unable to Reach Customer',
    'Canceled',
    'Reassigned',
    'Visit Scheduled',
    'Visit Completed',
    'Needs Second Opinion',
    'Under Research',
    'Billed',
    'Paid'
  ];

  const addresses = customerContacts.filter(contact => contact.type === 'address');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const form = e.target as HTMLFormElement;
    const addressId = form.address_id.value;

    if (!addressId) {
      setError('Please select a service address');
      setLoading(false);
      return;
    }

    try {
      const workOrderData = {
        customer_id: selectedCustomer,
        service_type: selectedService,
        brand: selectedService === 'Appliance' ? form.brand.value : null,
        svo: formData.svo,
        model_number: formData.model_number,
        serial_number: formData.serial_number,
        address_id: addressId,
        status: formData.status as string,
        priority: formData.priority as string,
        description: formData.description,
        technician_id: form.technician_id?.value || null,
        notes: formData.notes,
      };

      console.log('Submitting work order:', workOrderData);
      await onAdd(workOrderData);
      onClose();
    } catch (err) {
      console.error('Error creating work order:', err);
      setError(err instanceof Error ? err.message : 'Failed to create work order');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Create Work Order</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <span className="sr-only">Close</span>
              ×
            </button>
          </div>

          {error && (
            <div className="mb-4 p-4 text-sm text-red-800 bg-red-50 rounded-lg">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Customer
                  </label>
                  <div className="space-y-2">
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search customers..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <Search className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
                    </div>
                    <div className="bg-white border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
                      {filteredCustomers.map((customer) => (
                        <label
                          key={customer.id}
                          className={`flex items-start p-3 hover:bg-gray-50 cursor-pointer ${
                            selectedCustomer === customer.id ? 'bg-blue-50' : ''
                          }`}
                        >
                          <input
                            type="radio"
                            name="customer_id"
                            value={customer.id}
                            checked={selectedCustomer === customer.id}
                            onChange={() => setSelectedCustomer(customer.id)}
                            className="mt-1"
                          />
                          <div className="ml-3">
                            <p className="font-medium text-gray-900">
                              {customer.first_name} {customer.last_name}
                            </p>
                            <p className="text-sm text-gray-500">{customer.phone}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                {selectedCustomer && addresses.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Service Address
                    </label>
                    <div className="space-y-2">
                      {addresses.map((address) => (
                        <label key={address.id} className="flex items-start p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
                          <input
                            type="radio"
                            name="address_id"
                            value={address.id}
                            required
                            className="mt-1"
                          />
                          <div className="ml-3">
                            <p className="text-gray-900">{address.value}</p>
                            {address.label && <p className="text-sm text-gray-500">{address.label}</p>}
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <div className="grid grid-cols-2 gap-2">
                    {statusOptions.map(status => (
                      <button
                        key={status}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, status }))}
                        className={`p-2 rounded-lg border-2 transition-colors ${
                          formData.status === status
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-200 hover:border-gray-300 text-gray-700'
                        }`}
                      >
                        {status}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Service Type</label>
                  <select
                    name="service_type"
                    required
                    value={selectedService}
                    onChange={(e) => setSelectedService(e.target.value as ServiceType)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select a service</option>
                    {services.map((service) => (
                      <option key={service} value={service}>
                        {service}
                      </option>
                    ))}
                  </select>
                </div>

                {selectedService === 'Appliance' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Brand
                    </label>
                    <select
                      name="brand"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select a brand</option>
                      {brands.map((brand) => (
                        <option key={brand} value={brand}>
                          {brand}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {selectedService === 'Appliance' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        SVO (Service Order)
                      </label>
                      <input
                        type="text"
                        name="svo"
                        value={formData.svo}
                        onChange={(e) => setFormData(prev => ({ ...prev, svo: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter SVO number..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Model Number
                      </label>
                      <input
                        type="text"
                        name="model_number"
                        value={formData.model_number}
                        onChange={(e) => setFormData(prev => ({ ...prev, model_number: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter model number..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Serial Number
                      </label>
                      <input
                        type="text"
                        name="serial_number"
                        value={formData.serial_number}
                        onChange={(e) => setFormData(prev => ({ ...prev, serial_number: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter serial number..."
                      />
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Priority
                  </label>
                  <select
                    name="priority"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="emergency">Emergency</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    name="description"
                    required
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Describe the work needed..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Additional Notes (Optional)
                  </label>
                  <textarea
                    name="notes"
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Any additional notes or special instructions..."
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !selectedCustomer || addresses.length === 0}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Creating...' : 'Create Work Order'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};